import React, { ChangeEvent, useState } from 'react';
import { EventTypeInput } from '../../../types/ViewModel/EventTypeInput';
import eventInputUtils from '../../../utils/event/eventInputUtils';
import { DecimalErrorWrapper, DecimalInputOuterWrapper, DecimalInputWrapper } from './DecimalInput.styles';
import './DecimalInput.styles';
import { InputFieldWrapper, InputHeadingWrapper, InputWrapper } from '../EventInput.styles';
import { resolveHeading } from '../../../utils/event/headingOverrides';

type Props = {
    onChange: (ev: string | null, key: number, isEmpty: boolean, isValid: boolean) => void;
    evTypeInput: EventTypeInput;
    key: string;
};

const DecimalInputComponent: React.FC<Props> = (props) => {
    const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
    const [decimalErrorMessage, setDecimalErrorMessage] = useState<string | null>(null);
    const { isDecimalInputValid, validateNumber } = eventInputUtils();

    const isInputValid = (event: ChangeEvent<HTMLInputElement>): boolean => {
        if (event.target.value.length > 0) {
            let showError = false;
            const decValue = validateNumber(event.target.value);
            if (decValue) {
                const min = event.target.min !== '' ? event.target.min : null;
                const max = event.target.max !== '' ? event.target.max : null;

                if (min && decValue < +min) {
                    setDecimalErrorMessage('Value must be higher or equal to ' + min);
                    showError = true;
                }
                if (max && decValue > +max) {
                    setDecimalErrorMessage('Value must be lower or equal to ' + max);
                    showError = true;
                }
                setShowErrorMessage(showError);
                return !showError;
            }
            return false;
        } else {
            setShowErrorMessage(false);
        }
        return true;
    };

    const isNegativeNumberAllowed = (): boolean => {
        if (props.evTypeInput.inputDataTypeMaxMin && props.evTypeInput.inputDataTypeMaxMin.dataTypeDecimalMin) {
            return props.evTypeInput.inputDataTypeMaxMin.dataTypeDecimalMin < 0;
        }
        return true;
    };

    const isInputVerifiedRegEx = (regEx: string, trigger: string): boolean => {
        const regexp = new RegExp(regEx);
        return regexp.test(trigger);
    };

    const onNumberInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const inputString = event.target.value;
        event.preventDefault();

        const inpVerified = isInputVerifiedRegEx(event.target.pattern, inputString);

        if (inpVerified) {
            if (inputString === '-') {
                if (isNegativeNumberAllowed()) {
                    setShowErrorMessage(false);
                    props.onChange(inputString, props.evTypeInput.eventTypeInputId, false, false);
                } else {
                    setDecimalErrorMessage('Value must be higher or equal to ' + event.target.min);
                    setShowErrorMessage(true);
                    props.onChange(null, props.evTypeInput.eventTypeInputId, false, false);
                }
            } else {
                const isEmpty = inputString.length === 0;
                if (isInputValid(event)) {
                    props.onChange(inputString, props.evTypeInput.eventTypeInputId, isEmpty, true);
                } else {
                    props.onChange(inputString, props.evTypeInput.eventTypeInputId, isEmpty, false);
                }
            }
        }
    };

    const getRegexDecimalWithMinus = (): string => {
        let maxLength = 6;
        if (props.evTypeInput.inputDataTypeMaxMin && props.evTypeInput.inputDataTypeMaxMin.dataTypeDecimalMax) {
            maxLength = props.evTypeInput.inputDataTypeMaxMin.dataTypeDecimalMax.toString().length;
        }
        if (
            props.evTypeInput.inputDataTypeMaxMin &&
            props.evTypeInput.inputDataTypeMaxMin.dataTypeDecimalMin &&
            props.evTypeInput.inputDataTypeMaxMin.dataTypeDecimalMin < 0
        ) {
            if (maxLength < props.evTypeInput.inputDataTypeMaxMin.dataTypeDecimalMin.toString().length - 1) {
                maxLength = props.evTypeInput.inputDataTypeMaxMin.dataTypeDecimalMin.toString().length - 1;
            }
        }
        return '^(-)?\\d{0,' + maxLength + '}([\\.]\\d{0,3})?$';
    };

    const getMinValue = () => {
        if (props.evTypeInput.inputDataTypeMaxMin) {
            return props.evTypeInput.inputDataTypeMaxMin.dataTypeDecimalMin;
        }
        return undefined;
    };

    const getMaxValue = () => {
        if (props.evTypeInput.inputDataTypeMaxMin) {
            return props.evTypeInput.inputDataTypeMaxMin.dataTypeDecimalMax;
        }
        return undefined;
    };

    const getValue = (): string | null => {
        if (props.evTypeInput.eventTypeInputReg) {
            if (props.evTypeInput.eventTypeInputReg.decimalValueRegStr) {
                return props.evTypeInput.eventTypeInputReg.decimalValueRegStr;
            }
        }
        return null;
    };

    const checkIsValid = (evTypeInp: EventTypeInput, str: string | null): boolean => {
        return isDecimalInputValid(evTypeInp, str);
    };

    const inputElem = () => {
        return (
            <DecimalInputWrapper isNotValid={!checkIsValid(props.evTypeInput, getValue() ?? null)}>
                <input
                    type="text"
                    pattern={getRegexDecimalWithMinus()}
                    key={props.evTypeInput.eventTypeInputId}
                    onChange={(e) => onNumberInputChange(e)}
                    min={getMinValue()}
                    max={getMaxValue()}
                    value={getValue() ?? ''}
                    className="number"
                    autoComplete="off"
                />
            </DecimalInputWrapper>
        );
    };

    const getErrorMessage = () => {
        return decimalErrorMessage;
    };

    const errorMessageElem = () => {
        if (showErrorMessage) {
            return (
                <DecimalErrorWrapper>
                    <span>{getErrorMessage()}</span>
                </DecimalErrorWrapper>
            );
        }
        return null;
    };

    return (
        <>
            <InputWrapper>
                <InputHeadingWrapper>{resolveHeading(props.evTypeInput)}:</InputHeadingWrapper>
                <InputFieldWrapper>
                    <DecimalInputOuterWrapper>
                        {inputElem()}
                        {errorMessageElem()}
                    </DecimalInputOuterWrapper>
                </InputFieldWrapper>
            </InputWrapper>
        </>
    );
};

export default DecimalInputComponent;
